import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

export interface Goal {
  id: string;
  userId: string;
  goalType: string;
  title: string;
  description?: string;
  targetValue?: number;
  currentValue?: number;
  unit?: string;
  targetDate?: string;
  progressPercent: number;
  milestones?: any[];
  progressData?: any[];
  isActive: boolean;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface GoalStats {
  total: number;
  active: number;
  completed: number;
  completionRate: number;
}

export interface CreateGoalInput {
  goalType: string;
  title: string;
  description?: string;
  targetValue?: number;
  currentValue?: number;
  unit?: string;
  targetDate?: string;
}

export interface UpdateGoalInput {
  title?: string;
  description?: string;
  targetValue?: number;
  currentValue?: number;
  unit?: string;
  targetDate?: string;
  progressPercent?: number;
  isActive?: boolean;
  isCompleted?: boolean;
  milestones?: any[];
  progressData?: any[];
}

class GoalsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  async getGoals(filters?: {
    isActive?: boolean;
    isCompleted?: boolean;
    goalType?: string;
  }): Promise<Goal[]> {
    const params = new URLSearchParams();
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.isCompleted !== undefined) params.append('isCompleted', String(filters.isCompleted));
    if (filters?.goalType) params.append('goalType', filters.goalType);

    const response = await axios.get(
      `${API_URL}/api/goals?${params.toString()}`,
      this.getAuthHeaders()
    );
    return response.data.goals;
  }

  async getGoalStats(): Promise<GoalStats> {
    const response = await axios.get(
      `${API_URL}/api/goals/stats`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getGoal(goalId: string): Promise<Goal> {
    const response = await axios.get(
      `${API_URL}/api/goals/${goalId}`,
      this.getAuthHeaders()
    );
    return response.data.goal;
  }

  async createGoal(data: CreateGoalInput): Promise<Goal> {
    const response = await axios.post(
      `${API_URL}/api/goals`,
      data,
      this.getAuthHeaders()
    );
    return response.data.goal;
  }

  async updateGoal(goalId: string, data: UpdateGoalInput): Promise<Goal> {
    const response = await axios.put(
      `${API_URL}/api/goals/${goalId}`,
      data,
      this.getAuthHeaders()
    );
    return response.data.goal;
  }

  async deleteGoal(goalId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/api/goals/${goalId}`,
      this.getAuthHeaders()
    );
  }
}

export const goalsService = new GoalsService();
